import { Input } from '@base-ui/react';
import { Field } from '@base-ui/react/field';
import { Check, ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { Accordion } from '@/components/ui/Accordion';
import { Button } from '@/components/ui/Button';

interface LinkedInStepProps {
  onBack: () => void;
  onNext: (cookie: string) => void;
  onSkip: () => void;
}

export const LinkedInStep = ({ onBack, onNext, onSkip }: LinkedInStepProps) => {
  const [cookie, setCookie] = useState('');

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg lg:text-xl font-semibold">
          Hook up LinkedIn (optional)
        </h2>
        <p className="mt-1 text-sm lg:text-base text-foreground-muted">
          Paste your{' '}
          <code className="rounded-sm bg-surface px-1 py-0.5 font-mono text-xs lg:text-sm">
            li_at
          </code>{' '}
          cookie to scrape authenticated job postings.
        </p>
      </div>

      <Field.Root className="space-y-3">
        <Field.Label className="text-sm lg:text-base font-medium">
          li_at Cookie
        </Field.Label>
        <Input
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm lg:text-base transition-colors placeholder:text-foreground-muted/50 focus:border-accent focus:ring-2 focus:ring-accent/50 focus:outline-none"
          onChange={(event) => setCookie(event.target.value)}
          placeholder="Paste your li_at cookie value"
          type="password"
          value={cookie}
        />
      </Field.Root>

      <Accordion
        panelClassName="mt-2 space-y-2 rounded-md bg-surface-soft p-3 text-foreground-muted"
        title="How do I find my LinkedIn session cookie?"
        triggerClassName="border border-border text-foreground-muted hover:bg-surface"
      >
        <div className="mt-2 space-y-2 rounded-md border border-border bg-background p-4 text-sm lg:text-base text-foreground-muted">
          <ol className="list-inside list-decimal space-y-1.5">
            <li>
              Open LinkedIn in your browser and make sure you're logged in
            </li>
            <li>Open Developer Tools (F12 or Cmd+Option+I)</li>
            <li>Go to the Application tab (Chrome) or Storage tab (Firefox)</li>
            <li>Expand Cookies in the sidebar and select www.linkedin.com</li>
            <li>
              Find the cookie named{' '}
              <code className="rounded-sm bg-surface px-1 py-0.5 text-xs lg:text-sm">
                li_at
              </code>{' '}
              and copy its value
            </li>
          </ol>
          <p className="mt-3 text-xs lg:text-sm text-foreground-muted/70">
            Note: This cookie expires periodically. You may need to update it
            when it stops working.
          </p>
        </div>
      </Accordion>

      <div className="flex items-center justify-between">
        <Button onClick={onBack} size="md" type="button" variant="ghost">
          <ChevronLeft className="size-3.5" /> Back
        </Button>
        <div className="flex gap-2">
          <Button onClick={onSkip} size="md" type="button" variant="ghost">
            Skip
          </Button>
          <Button
            disabled={!cookie.trim()}
            onClick={() => onNext(cookie)}
            size="md"
            type="button"
            variant="primary"
          >
            Finish setup <Check className="size-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};
